/**
 * Deterministic vocal-identity injection for the Suno Style / Exclude fields.
 *
 * The vocal profile used to reach Suno's Style box only through the LLM's
 * paraphrase: the profile's META_STYLES were handed to the song architect as
 * background, and the architect condensed ~800 chars of voice description
 * into two or three generic words ("baritone male vocal") buried behind the
 * genre/gear tags. The rich description only survived inside the
 * {LAYER-1 VOCAL PHYSICS} lyrics block, which Suno weights far less than the
 * Style box. Net effect: every custom voice collapsed back to the default.
 *
 * This puts the profile's own style tags, verbatim, at the FRONT of the Style
 * field (a reserved slot), puts its excludes at the front of the Exclude
 * field, and drops any architect-written style tag that the profile's own
 * exclude list rules out. Same deterministic post-generation pattern as
 * personaLock.ts.
 *
 * AUTO-090: the genres picked in Studio (formData.blends, which Studio Roulette
 * fills) go in front of everything, ahead of the vocal profile. The architect
 * sometimes dropped a picked genre from its tags altogether ("A Cappella" never
 * reached the Style box of a song rolled with it), and Suno weights the start
 * of the field most.
 */
import type { HookhouseFormData, ParsedSunoOutput } from '@/types';
import type { PersonaOverride } from '@/types/persona.types';
import { extractBlock } from '@/services/llmShared';

/** Max chars of the 1000-char Style field reserved for the vocal profile. */
export const VOCAL_STYLE_SLOT = 350;
const STYLE_MAX = 1000;
const EXCLUDE_MAX = 1000;

export const splitTags = (s: string): string[] =>
  // Newlines too: the architect sometimes writes the list as two paragraphs.
  s.split(/[,\n]/).map(t => t.trim()).filter(Boolean);

/** Whole tags, in order, until the next one would exceed `max` chars joined. */
export const takeTags = (tags: string[], max: number): string[] => {
  const kept: string[] = [];
  let len = 0;
  for (const t of tags) {
    const next = kept.length === 0 ? t.length : len + 2 + t.length;
    if (next > max) break;
    kept.push(t);
    len = next;
  }
  return kept;
};

const readMetaLine = (arch: string, key: string): string => {
  const m = arch.match(new RegExp(`^${key}:\\s*(.+)$`, 'm'));
  return m ? m[1].trim() : '';
};

// ---------------------------------------------------------------------------
// Lyric-theme filter (AUTO-039 / Forgejo #119). The architect routinely moves
// the form's AVOID/THEMES text into EXCLUDED STYLES (and occasionally SUNO
// TAGS), reworded -- "workplace specifics (cubicles...)" comes back as
// "cubicle meetings bosses by name". Suno can't hear a theme, and those tags
// push real sound excludes past the 1000-char trim. Prompt-only rules didn't
// hold (3 of 4 runs still leaked), so this filters deterministically.
//
// A tag is dropped when it shares >= 2 significant word-stems with the theme
// text. Content-leaning words ("imagery", "drama"...) count as a hit on their
// own; words that never describe sound ("themes", "narrative", "lyrics")
// mark the tag outright. Requiring two hits is the safety margin: a
// sound tag like "key change" shares only one word with "hope or change".
// A tag whose every significant word is a theme word is also dropped.
// ---------------------------------------------------------------------------
const STOPWORDS = new Set([
  'with', 'from', 'that', 'this', 'versus', 'into', 'like', 'your', 'their', 'them', 'have',
  'what', 'when', 'where', 'which', 'while', 'about', 'over', 'under', 'than', 'then', 'there',
  'these', 'those', 'only', 'just', 'very', 'more', 'most', 'each', 'other', 'some', 'such',
  'being', 'been', 'were', 'will', 'would', 'could', 'should', 'still', 'actually', 'also',
  'song', 'songs', 'style', 'styles', 'sound', 'sounds', 'vocal', 'vocals', 'voice',
]);
// Words that never describe sound -- one is enough to mark a tag as a theme.
const STRONG_MARKERS = new Set([
  'narrative', 'narratives', 'theme', 'themes', 'thematic', 'storytelling', 'lyrics', 'lyricism',
  'subject', 'plot',
]);
// Content-leaning words that count as one hit (ambiguous alone: "lyrical lead").
const CONTENT_MARKERS = new Set([
  ...STRONG_MARKERS, 'imagery', 'story', 'lyric', 'lyrical', 'references', 'reference',
  'drama', 'nostalgia', 'arc', 'arcs', 'redemption',
]);

const stem = (w: string): string => (w.length >= 6 ? w.slice(0, 5) : w.replace(/s$/, ''));
const wordsOf = (s: string): string[] => s.toLowerCase().split(/[^a-z]+/).filter(w => w.length >= 4 && !STOPWORDS.has(w));

export function buildThemeStems(...texts: Array<string | undefined>): Set<string> {
  const stems = new Set<string>();
  for (const t of texts) for (const w of wordsOf(t || '')) if (!CONTENT_MARKERS.has(w)) stems.add(stem(w));
  return stems;
}

export function isThemeTag(tag: string, themeStems: Set<string>): boolean {
  if (themeStems.size === 0) return false;
  let hits = 0;
  const seen = new Set<string>();
  for (const w of wordsOf(tag)) {
    if (STRONG_MARKERS.has(w)) return true;
    const st = stem(w);
    if (seen.has(st)) continue;
    seen.add(st);
    // Prefix match too, so AVOID "hope" catches "hopeful" (stems are 4-5 chars).
    if (CONTENT_MARKERS.has(w) || themeStems.has(st) || [...themeStems].some(t => t.length >= 4 && w.startsWith(t))) hits++;
    if (hits >= 2) return true;
  }
  // A tag made ONLY of theme words ("cubicles", "resolution") is a theme too.
  return seen.size > 0 && hits === seen.size;
}

/** Whether the vocal-profile slot will be filled -- same rule applyVocalIdentityToStyle uses. */
export function isVocalSlotActive(
  vocalArchitecture: string | undefined,
  data: Pick<HookhouseFormData, 'stylePreset' | 'isInstrumental'>,
  personaOverride: PersonaOverride | null | undefined,
): boolean {
  const isPresetOverride = !personaOverride && !!data.stylePreset && data.stylePreset !== 'None';
  if (!vocalArchitecture || personaOverride || isPresetOverride || data.isInstrumental) return false;
  return splitTags(readMetaLine(vocalArchitecture, 'META_STYLES')).length > 0;
}

export function applyVocalIdentityToStyle(
  result: ParsedSunoOutput,
  vocalArchitecture: string | undefined,
  data: Pick<HookhouseFormData, 'stylePreset' | 'isInstrumental'> & Partial<Pick<HookhouseFormData, 'themesToAvoid' | 'themesToHit' | 'blends'>>,
  personaOverride: PersonaOverride | null | undefined,
): ParsedSunoOutput {
  // Mirror buildSunoPromptInput: the vocal architecture is only used when no
  // persona / style preset overrides it and the song has vocals.
  const isPresetOverride = !personaOverride && !!data.stylePreset && data.stylePreset !== 'None';
  const vocalActive = !!vocalArchitecture && !personaOverride && !isPresetOverride && !data.isInstrumental;

  const allVocalTags = vocalActive ? splitTags(readMetaLine(vocalArchitecture!, 'META_STYLES')) : [];
  const vocalTags = takeTags(allVocalTags, VOCAL_STYLE_SLOT);
  const vocalExcludes = vocalActive ? splitTags(readMetaLine(vocalArchitecture!, 'META_EXCLUDE')) : [];
  const themeStems = buildThemeStems(data.themesToAvoid, data.themesToHit);
  const hasVocal = vocalTags.length > 0 || vocalExcludes.length > 0;
  const lower = (s: string) => s.toLowerCase();
  // The picked genres lead the Style field. A persona or a style preset replaces the genre text, so no slot then.
  const genreTags = (!personaOverride && !isPresetOverride ? data.blends ?? [] : [])
    .map(g => g.trim())
    .filter((g, i, all) => g !== '' && all.findIndex(x => lower(x.trim()) === lower(g)) === i);
  if (!hasVocal && themeStems.size === 0 && genreTags.length === 0) return result;

  const genreSet = new Set(genreTags.map(lower));
  // Dedupe against the FULL profile list, not just the slot -- the architect
  // often copies the whole META_STYLES line into its own tags, and the
  // overflow past the slot would otherwise come back in as a second copy.
  const vocalSet = new Set(allVocalTags.map(lower));
  const frontVocalTags = vocalTags.filter(t => !genreSet.has(lower(t)));
  const excludeTerms = vocalExcludes.map(lower).filter(t => t.length >= 4);

  // Work from the architect's UNTRIMMED tags: the parsers already cut
  // `style` to 1000 chars, and the architect routinely copies the whole
  // profile into its tags first -- so the trimmed version is mostly profile
  // copies, which the dedupe below removes, leaving almost no genre/production.
  const rawStyle = (result.fullResponse && extractBlock(result.fullResponse, 'SUNO TAGS:')) || result.style || '';
  const rawExclude = (result.fullResponse && extractBlock(result.fullResponse, 'EXCLUDED STYLES:')) || result.excludedStyle || '';

  // Profile tags (vocalTags / vocalExcludes) are never theme-filtered.
  const llmStyle = splitTags(rawStyle).filter(t => {
    const lt = lower(t);
    return !vocalSet.has(lt) && !genreSet.has(lt) && !excludeTerms.some(term => lt.includes(term)) && !isThemeTag(t, themeStems);
  });
  const style = takeTags([...genreTags, ...frontVocalTags, ...llmStyle], STYLE_MAX).join(', ');

  const excludeSeen = new Set<string>();
  const excluded = takeTags(
    [...vocalExcludes, ...splitTags(rawExclude).filter(t => !isThemeTag(t, themeStems))].filter(t => {
      const lt = lower(t);
      if (excludeSeen.has(lt)) return false;
      excludeSeen.add(lt);
      return true;
    }),
    EXCLUDE_MAX,
  ).join(', ');

  const next: ParsedSunoOutput = { ...result, style, excludedStyle: excluded };
  if (result.charLimits) {
    next.charLimits = {
      ...result.charLimits,
      styleTags: { count: style.length, max: STYLE_MAX, over: false },
      excludedStyles: { count: excluded.length, max: EXCLUDE_MAX, over: false },
    };
  }
  return next;
}
